"use client"

import {
  Box,
  Container,
  Grid,
  HStack,
  SimpleGrid,
  Skeleton,
  type SkeletonProps,
  Stack,
  VStack,
} from "@chakra-ui/react"
import { type ReactNode } from "react"

import { FeaturedWorkCardSkeleton } from "@/components/Cards/FeaturedWorkCard"
import SkillsBentoSkeleton from "@/sections/SkillsBentoSkeleton"

type PageSkeletonName =
  | "about"
  | "contact"
  | "cookies"
  | "cv"
  | "home"
  | "privacy-policy"
  | "search"
  | "skills-bento"
  | "terms-of-service"
  | "work"
  | "work-article"

interface PageSkeletonProps {
  children?: ReactNode
  loading?: boolean
  name: PageSkeletonName | string
}

function Bone(props: SkeletonProps) {
  return <Skeleton rounded="lg" variant="shine" {...props} />
}

function Lines({
  count = 3,
  widths = ["100%", "86%", "64%"],
}: {
  count?: number
  widths?: Array<number | string>
}) {
  return (
    <Stack gap={3} width="full">
      {Array.from({ length: count }).map((_, index) => (
        <Bone
          h={{ base: 3, md: 4 }}
          key={index}
          rounded="full"
          w={widths[index % widths.length]}
        />
      ))}
    </Stack>
  )
}

function PageFrame({
  children,
  maxW = "8xl",
}: {
  children: ReactNode
  maxW?: string
}) {
  return (
    <Container maxW={maxW} my={6} width="full">
      <VStack align="stretch" aria-busy="true" gap={6}>
        {children}
      </VStack>
    </Container>
  )
}

function FeaturedWorkList({
  count = 3,
  onPaper = false,
}: {
  count?: number
  onPaper?: boolean
}) {
  return (
    <Stack gap={{ base: 6, md: 8 }} w="full">
      {Array.from({ length: count }).map((_, index) => (
        <FeaturedWorkCardSkeleton key={index} onPaper={onPaper} />
      ))}
    </Stack>
  )
}

function HomeSkeleton() {
  return (
    <VStack
      alignItems="center"
      aria-busy="true"
      bgColor="bg.paper"
      gap={0}
      justifyContent="flex-start"
      py={6}
      width="full"
    >
      <Stack
        alignItems="center"
        bgColor="bg.paper"
        flexDirection={{ base: "column-reverse", md: "row" }}
        gap={{ base: 6, md: 6, lg: 8 }}
        justifyContent="center"
        maxW="8xl"
        mx="auto"
        px={{ base: 8, md: 12, lg: 16 }}
        py={16}
        rounded="2xl"
        w={{ base: "calc(100% - 1rem)", md: "calc(100% - 3rem)" }}
      >
        <VStack
          alignItems="stretch"
          gap={{ base: 3, md: 4 }}
          w={{ base: "100%", md: "60%" }}
        >
          <Stack gap={3}>
            <Bone
              h={{ base: 10, sm: 12, md: 14, lg: 16 }}
              rounded="xl"
              w="92%"
            />
            <Bone
              h={{ base: 10, sm: 12, md: 14, lg: 16 }}
              rounded="xl"
              w="78%"
            />
            <Bone
              h={{ base: 10, sm: 12, md: 14, lg: 16 }}
              rounded="xl"
              w="58%"
            />
          </Stack>
          <Lines count={2} widths={["92%", "76%"]} />
          <HStack gap={{ base: 2, md: 4 }} mt={8} wrap="wrap">
            <Bone
              h={{ base: 9, lg: 12 }}
              rounded="md"
              w={{ base: "full", sm: "148px" }}
            />
            <Bone
              h={{ base: 9, lg: 12 }}
              rounded="md"
              w={{ base: "full", sm: "124px" }}
            />
          </HStack>
        </VStack>
        <Bone
          h={{ base: "210px", md: "320px", lg: "360px" }}
          maxW={{ md: "480px", lg: "600px" }}
          rounded="2xl"
          w={{ base: "100%", md: "40%" }}
        />
      </Stack>

      <Box
        as="section"
        borderBottomColor="border"
        borderBottomWidth="1px"
        borderTopColor="border"
        borderTopWidth="1px"
        mt={6}
        w="full"
      >
        <SimpleGrid
          borderColor="border"
          borderInlineWidth="1px"
          columns={{ base: 2, lg: 4 }}
          maxW="1440px"
          mx="auto"
          px={{ base: 6, lg: 0 }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <VStack
              alignItems="flex-start"
              borderColor="border"
              borderLeftWidth={{
                base: index % 2 === 0 ? "0" : "1px",
                lg: index % 4 === 0 ? "0" : "1px",
              }}
              borderTopWidth={{ base: index >= 2 ? "1px" : "0", lg: "0" }}
              gap={4}
              key={index}
              p={{ base: 8, lg: 10 }}
            >
              <Bone h={{ base: 10, md: 12 }} rounded="lg" w="92px" />
              <Lines count={2} widths={["88%", "64%"]} />
            </VStack>
          ))}
        </SimpleGrid>
      </Box>

      <Box
        as="section"
        borderBottom="1px solid {colors.border}"
        position="relative"
        width="full"
      >
        <HStack
          borderLeft="1px solid {colors.border}"
          borderRight="1px solid {colors.border}"
          maxW="1440px"
          mx="auto"
          py={4}
          w="full"
        >
          <Box borderRight="1px solid {colors.border}" px={4} py={2}>
            <Bone h={4} rounded="full" w="132px" />
          </Box>
          <HStack flex="1" gap={8} overflow="hidden" px={6}>
            {Array.from({ length: 7 }).map((_, index) => (
              <Bone
                h={4}
                key={index}
                minW={`${72 + (index % 3) * 20}px`}
                rounded="full"
              />
            ))}
          </HStack>
        </HStack>
      </Box>

      <VStack
        as="section"
        bgColor="bg.paper"
        borderLeft="1px solid {colors.border}"
        borderRight="1px solid {colors.border}"
        gap={0}
        maxW="8xl"
        mt={6}
        rounded="0"
        w={{ base: "calc(100% - 1rem)", md: "calc(100% - 3rem)" }}
      >
        <HStack
          justifyContent="space-between"
          px={{ base: 3, md: 4, lg: 12 }}
          py={{ base: 6, md: 8, lg: 10 }}
          w="full"
        >
          <Bone
            h={{ base: 8, md: 12 }}
            rounded="lg"
            w={{ base: "220px", md: "360px" }}
          />
        </HStack>
        <FeaturedWorkList count={3} onPaper />
        <HStack
          justifyContent="center"
          px={{ base: 3, md: 4, lg: 12 }}
          py={{ base: 6, lg: 8 }}
          w="full"
        >
          <Bone h={5} rounded="full" w="168px" />
        </HStack>
      </VStack>
    </VStack>
  )
}

function WorkSkeleton() {
  return (
    <VStack
      alignItems="center"
      aria-busy="true"
      bgColor="bg.paper"
      gap={0}
      justifyContent="flex-start"
      width="full"
    >
      <VStack
        as="section"
        bgColor="bg.paper"
        borderBottom="1px solid {colors.border}"
        display="flex"
        gap={0}
        rounded="0"
        w="full"
      >
        <Stack
          alignItems="center"
          borderLeft="1px solid {colors.border}"
          borderRight="1px solid {colors.border}"
          flexDirection={{ base: "column", lg: "row" }}
          gap={[0, 0, 0, "2rem", "4", "4rem"]}
          maxW="8xl"
          mx="auto"
          pb={[1, 1, 2]}
          pt={[2, 2, 4]}
          w="full"
        >
          <HStack
            mb={[2, 2, 0]}
            px={{ base: 4, md: 8 }}
            width={{ base: "100%", lg: "50%" }}
          >
            <VStack
              alignItems={{ base: "center", lg: "flex-start" }}
              gap={4}
              w="full"
            >
              <Bone h={4} rounded="full" w="72px" />
              <Bone
                h={{ base: 10, md: 14 }}
                maxW="80%"
                rounded="lg"
                w="280px"
              />
              <Lines count={2} widths={["82%", "62%"]} />
            </VStack>
          </HStack>
          <HStack
            flexGrow="1"
            justifyContent="center"
            pr={[0, 0, 0, "2rem", "4rem"]}
          >
            <Bone
              h={["128px", "168px", "168px", "190px", "248px"]}
              rounded="xl"
              w={["290px", "420px", "380px", "500px", "620px"]}
            />
          </HStack>
        </Stack>
      </VStack>

      <HStack
        borderBottom="1px solid {colors.border}"
        gap={0}
        justifyContent="space-between"
        maxW="8xl"
        mx="auto"
        w="full"
        wrap="wrap"
      >
        <HStack gap={0} wrap="wrap">
          <Bone h={10} rounded="0" w="132px" />
          <Bone h={10} rounded="0" w="92px" />
          <Bone h={5} ml={3} rounded="full" w="84px" />
        </HStack>
        <Bone h={10} rounded="0" w="52px" />
      </HStack>

      <Box maxW="8xl" mx="auto" w="full">
        <FeaturedWorkList count={6} onPaper />
      </Box>
    </VStack>
  )
}

function WorkArticleSkeleton() {
  return (
    <VStack align="stretch" aria-busy="true" bg="bg.paper" gap={0} w="full">
      <VStack
        alignItems="stretch"
        borderLeft="1px solid {colors.border}"
        borderRight="1px solid {colors.border}"
        gap={0}
        maxW="8xl"
        mx="auto"
        pt={{ base: 8, md: 16, lg: 20, xl: 28 }}
        w="full"
      >
        <HStack
          justifyContent="space-between"
          px={{ base: 8, md: 10, lg: 24, xl: 40 }}
          w="full"
        >
          <HStack gap={2}>
            <Bone h={5} rounded="full" w="72px" />
            <Bone h={5} rounded="full" w="96px" />
          </HStack>
          <Bone h={5} rounded="full" w="112px" />
        </HStack>

        <Stack
          gap={4}
          px={{ base: 8, md: 10, lg: 24, xl: 40 }}
          py={{ base: 8, md: 10 }}
        >
          <Bone h={{ base: 12, md: 14, lg: 16, xl: 20 }} rounded="lg" w="86%" />
          <Bone h={{ base: 12, md: 14, lg: 16, xl: 20 }} rounded="lg" w="62%" />
          <Lines count={2} widths={["82%", "66%"]} />
        </Stack>

        <Stack
          borderBottom="1px solid {colors.border}"
          borderTop="1px solid {colors.border}"
          direction={{ base: "column", md: "row" }}
          gap={0}
          w="full"
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <VStack
              alignItems="flex-start"
              borderLeft={{
                base: "0",
                md: index === 0 ? "0" : "1px solid {colors.border}",
              }}
              borderTop={{
                base: index === 0 ? "0" : "1px solid {colors.border}",
                md: "0",
              }}
              flex="1"
              gap={3}
              key={index}
              px={{ base: 8, md: 8, lg: 10, xl: 12 }}
              py={{ base: 5, md: 6 }}
            >
              <Bone h={3} rounded="full" w="104px" />
              <Bone h={9} rounded="md" w={{ base: "126px", md: "148px" }} />
              <Bone h={3} rounded="full" w="76%" />
            </VStack>
          ))}
        </Stack>

        <Box
          aspectRatio="16 / 9"
          overflow="hidden"
          position="relative"
          w="full"
        >
          <Bone h="full" rounded="0" w="full" />
        </Box>

        <Stack
          alignItems="stretch"
          borderTop="1px solid {colors.border}"
          direction={{ base: "column", lg: "row" }}
          gap={0}
          justifyContent="space-between"
          w="full"
        >
          <HStack
            borderTop={{
              base: "1px solid {colors.border}",
              lg: "1px solid transparent",
            }}
            flex="1"
            gap={2}
            px={4}
            py={3}
            wrap="wrap"
          >
            <Bone h={4} rounded="full" w="18px" />
            <Bone h={4} rounded="full" w="84px" />
            <Bone h={4} rounded="full" w="112px" />
            <Bone h={4} rounded="full" w="96px" />
            <Bone h={4} rounded="full" w="132px" />
          </HStack>
          <HStack
            borderLeft={{ base: "0", lg: "1px solid {colors.border}" }}
            borderTop={{
              base: "1px solid {colors.border}",
              lg: "1px solid transparent",
            }}
            gap={3}
            minH="64px"
            px={4}
          >
            <Bone h={10} rounded="full" w={10} />
            <VStack alignItems="flex-start" gap={2}>
              <Bone h={4} rounded="full" w="118px" />
              <Bone h={3} rounded="full" w="164px" />
            </VStack>
          </HStack>
          <HStack
            borderLeft={{ base: "0", lg: "1px solid {colors.border}" }}
            borderTop={{
              base: "1px solid {colors.border}",
              lg: "1px solid transparent",
            }}
            gap={0}
            minH="64px"
          >
            {Array.from({ length: 4 }).map((_, index) => (
              <Box
                borderLeft={index === 0 ? "0" : "1px solid {colors.border}"}
                key={index}
                px={4}
                py={4}
              >
                <Bone h={6} rounded="full" w={6} />
              </Box>
            ))}
          </HStack>
        </Stack>

        <Stack
          alignItems="stretch"
          direction={{ base: "column", lg: "row" }}
          gap={0}
          justifyContent="space-between"
          w="full"
        >
          <VStack
            alignItems="flex-start"
            borderTop="1px solid {colors.border}"
            gap={6}
            maxW={{ base: "100%", lg: "30%" }}
            px={{ base: 8, md: 10, lg: 10, xl: 12 }}
            py={{ base: 10, md: 12 }}
            w="full"
          >
            <VStack alignItems="flex-start" gap={2}>
              <Bone h={3} rounded="full" w="48px" />
              <Bone h={4} rounded="full" w="190px" />
            </VStack>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 2 }} gap={6} w="full">
              <VStack alignItems="flex-start" gap={2}>
                <Bone h={3} rounded="full" w="70px" />
                <Bone h={4} rounded="full" w="104px" />
              </VStack>
              <VStack alignItems="flex-start" gap={2}>
                <Bone h={3} rounded="full" w="64px" />
                <Bone h={4} rounded="full" w="118px" />
              </VStack>
            </SimpleGrid>
            <VStack alignItems="flex-start" gap={2}>
              <Bone h={3} rounded="full" w="62px" />
              <Bone h={4} rounded="full" w="142px" />
            </VStack>
            <VStack alignItems="flex-start" gap={2}>
              <Bone h={3} rounded="full" w="46px" />
              <Bone h={4} rounded="full" w="178px" />
            </VStack>
            <VStack alignItems="flex-start" gap={2}>
              <Bone h={3} rounded="full" w="70px" />
              <HStack gap={2} wrap="wrap">
                <Bone h={6} rounded="sm" w="96px" />
                <Bone h={6} rounded="sm" w="132px" />
                <Bone h={6} rounded="sm" w="112px" />
              </HStack>
            </VStack>
          </VStack>
          <VStack
            alignItems="flex-start"
            borderLeft={{ base: "0", lg: "1px solid {colors.border}" }}
            borderTop="1px solid {colors.border}"
            gap={0}
            maxW={{ base: "100%", lg: "70%" }}
            w="full"
          >
            <VStack
              alignItems="stretch"
              gap={8}
              px={{ base: 8, md: 10, lg: 24, xl: 24 }}
              py={{ base: 16 }}
              w="full"
            >
              <Bone h={8} rounded="md" w="112px" />
              <Lines count={4} widths={["94%", "88%", "92%", "76%"]} />
            </VStack>
          </VStack>
        </Stack>
      </VStack>

      <Grid
        borderLeft="1px solid {colors.border}"
        borderRight="1px solid {colors.border}"
        borderTop="1px solid {colors.border}"
        gap={0}
        maxW="8xl"
        mx="auto"
        templateColumns={{ base: "1fr", lg: "30% 70%" }}
        w="full"
      >
        <VStack
          alignItems="flex-start"
          borderRight={{ base: "0", lg: "1px solid {colors.border}" }}
          gap={4}
          p={{ base: 8, md: 10, lg: 12 }}
        >
          <Bone h={8} rounded="md" w="144px" />
        </VStack>
        <VStack align="stretch" gap={5} p={{ base: 8, md: 10, lg: 12 }}>
          <Lines count={6} widths={["100%", "94%", "88%", "72%"]} />
          <Bone aspectRatio="16 / 9" rounded="lg" w="full" />
          <Lines count={6} widths={["98%", "92%", "80%"]} />
        </VStack>
      </Grid>
    </VStack>
  )
}

function AboutSkeleton() {
  return (
    <PageFrame>
      <Grid
        bg="bg.paper"
        gap={8}
        p={{ base: 6, md: 10 }}
        rounded="2xl"
        templateColumns={{ base: "1fr", lg: "0.75fr 1.25fr" }}
      >
        <VStack align="stretch" gap={5}>
          <Bone h={{ base: "280px", md: "420px" }} rounded="2xl" />
          <HStack gap={3}>
            <Bone h={10} rounded="full" w={10} />
            <Bone h={10} rounded="full" w={10} />
            <Bone h={10} rounded="full" w={10} />
          </HStack>
        </VStack>
        <VStack align="stretch" gap={6} justify="center">
          <Bone h={6} rounded="full" w="118px" />
          <Bone h={{ base: 11, md: 14 }} rounded="xl" w="78%" />
          <Lines count={5} widths={["96%", "88%", "92%", "76%", "62%"]} />
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Bone h="86px" key={index} rounded="xl" />
            ))}
          </SimpleGrid>
        </VStack>
      </Grid>
    </PageFrame>
  )
}

function ContactSkeleton() {
  return (
    <PageFrame maxW="6xl">
      <Grid
        bg="bg.paper"
        gap={8}
        p={{ base: 6, md: 10, lg: 14 }}
        rounded="2xl"
        templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
      >
        <VStack align="stretch" gap={10} justify="space-between">
          <VStack align="stretch" gap={5}>
            <Bone h={14} rounded="xl" w={14} />
            <Bone h={{ base: 10, md: 12 }} rounded="xl" w="82%" />
            <Bone h={{ base: 10, md: 12 }} rounded="xl" w="64%" />
            <Lines count={3} widths={["92%", "84%", "60%"]} />
          </VStack>
          <HStack gap={4}>
            <Bone h={10} rounded="full" w={10} />
            <Bone h={10} rounded="full" w={10} />
            <Bone h={10} rounded="full" w={10} />
          </HStack>
        </VStack>
        <VStack align="stretch" gap={4}>
          <Bone h={12} rounded="lg" />
          <Bone h={12} rounded="lg" />
          <Bone h={12} rounded="lg" />
          <Bone h={36} rounded="lg" />
          <Bone alignSelf="flex-end" h={11} rounded="full" w="148px" />
        </VStack>
      </Grid>
    </PageFrame>
  )
}

function LegalSkeleton({
  variant,
}: {
  variant: "cookies" | "privacy" | "terms"
}) {
  const isPrivacy = variant === "privacy"
  const isCookies = variant === "cookies"
  const sectionCount = isPrivacy ? 8 : isCookies ? 6 : 5

  return (
    <PageFrame>
      <VStack align="stretch" gap={6}>
        <VStack align="stretch" bg="bg.paper" gap={4} p={8} rounded="2xl">
          <Bone
            h={9}
            rounded="lg"
            w={{
              base: "72%",
              md: variant === "terms" ? "300px" : isCookies ? "340px" : "380px",
            }}
          />
          <Lines
            count={isPrivacy || isCookies ? 2 : 1}
            widths={["82%", "64%"]}
          />
        </VStack>
        <VStack align="stretch" bg="bg.paper" gap={5} p={8} rounded="2xl">
          {!isCookies && (
            <HStack justify="space-between">
              <Bone h={4} rounded="full" w="168px" />
              <Bone h={4} rounded="full" w={isPrivacy ? "168px" : "118px"} />
            </HStack>
          )}
          {isCookies && (
            <VStack align="stretch" gap={3}>
              <Bone h={7} rounded="lg" w="190px" />
              <HStack align="flex-start" gap={3}>
                <Bone h={3} mt={2} rounded="full" w={3} />
                <Lines count={2} widths={["92%", "70%"]} />
              </HStack>
              <HStack align="flex-start" gap={3}>
                <Bone h={3} mt={2} rounded="full" w={3} />
                <Lines count={2} widths={["86%", "62%"]} />
              </HStack>
            </VStack>
          )}
          {Array.from({ length: sectionCount }).map((_, index) => (
            <VStack align="stretch" gap={3} key={index}>
              <Bone
                h={7}
                rounded="lg"
                w={
                  variant === "terms"
                    ? index % 2 === 0
                      ? "220px"
                      : "180px"
                    : index % 2 === 0
                      ? "240px"
                      : "200px"
                }
              />
              <Lines
                count={isPrivacy ? 4 : 3}
                widths={
                  isCookies ? ["96%", "88%", "72%"] : ["100%", "94%", "76%"]
                }
              />
            </VStack>
          ))}
        </VStack>
      </VStack>
    </PageFrame>
  )
}

function CvSkeleton() {
  return (
    <PageFrame maxW="640px">
      <VStack align="stretch" gap={10}>
        <VStack align="stretch" gap={5}>
          <Bone h={20} rounded="full" w={20} />
          <Bone h={12} maxW="100%" rounded="xl" w="260px" />
          <Bone h={4} rounded="full" w="180px" />
          <Bone h={4} rounded="full" w="128px" />
        </VStack>
        {Array.from({ length: 7 }).map((_, index) => (
          <Grid
            borderColor="border"
            borderTopWidth={index === 0 ? "0" : "1px"}
            gap={4}
            key={index}
            pt={index === 0 ? 0 : 6}
            templateColumns={{ base: "1fr", md: "140px 1fr" }}
          >
            <Bone h={4} rounded="full" w="86px" />
            <VStack align="stretch" gap={3}>
              <Bone h={5} rounded="md" w="78%" />
              <Bone h={4} rounded="full" w="54%" />
            </VStack>
          </Grid>
        ))}
      </VStack>
    </PageFrame>
  )
}

function SearchSkeleton() {
  return (
    <PageFrame>
      <VStack align="stretch" gap={6}>
        <VStack align="stretch" bg="bg.paper" gap={4} p={8} rounded="2xl">
          <Bone h={9} rounded="lg" w="170px" />
          <Bone h={4} maxW="100%" rounded="full" w="240px" />
        </VStack>
        <VStack align="stretch" bg="bg.paper" gap={5} p={8} rounded="2xl">
          <Bone h={10} rounded="lg" w="220px" />
          <Bone h={12} rounded="lg" />
          <Bone h={12} rounded="lg" />
          <Bone h={12} rounded="lg" />
        </VStack>
      </VStack>
    </PageFrame>
  )
}

function FallbackSkeleton() {
  return (
    <PageFrame>
      <Box bg="bg.paper" p={8} rounded="2xl">
        <Lines count={6} />
      </Box>
    </PageFrame>
  )
}

function renderSkeleton(name: string) {
  switch (name) {
    case "about":
      return <AboutSkeleton />
    case "contact":
      return <ContactSkeleton />
    case "cookies":
      return <LegalSkeleton variant="cookies" />
    case "cv":
      return <CvSkeleton />
    case "home":
      return <HomeSkeleton />
    case "privacy-policy":
      return <LegalSkeleton variant="privacy" />
    case "search":
      return <SearchSkeleton />
    case "skills-bento":
      return (
        <PageFrame>
          <SkillsBentoSkeleton />
        </PageFrame>
      )
    case "terms-of-service":
      return <LegalSkeleton variant="terms" />
    case "work":
      return <WorkSkeleton />
    case "work-article":
      return <WorkArticleSkeleton />
    default:
      return <FallbackSkeleton />
  }
}

export default function PageSkeleton({
  children,
  loading = false,
  name,
}: PageSkeletonProps) {
  if (!loading) {
    return <>{children}</>
  }

  return renderSkeleton(name)
}
